/* eslint-disable @typescript-eslint/naming-convention */

import { observer } from '@formily/react';
import { IAnyObject, isSuccess, isVacuous } from '@yimoka/shared';
import { IStore, IStoreResponse } from '@yimoka/store';
import React, { PropsWithChildren, useMemo } from 'react';

import { ErrorProps, ISize } from '../../context/config';
import { useComponents } from '../../hooks/components';
import { useStore } from '../../hooks/store';

export type IEntityResponseProps = PropsWithChildren<{
  store?: IStore | false
  skeleton?: IAnyObject | false
  returnIndex?: boolean
  again?: boolean
  /** 自定义重试函数；未提供时默认调用当前 Store 的 fetch。 */
  onAgain?: ErrorProps['onAgain']
  load?: IAnyObject | boolean
  size?: ISize
  loading?: boolean
  response?: IStoreResponse
  /** 错误展示组件的扩展属性；请求状态相关属性仍由 EntityResponse 统一控制。 */
  errorProps?: Partial<ErrorProps>
}>

/** 根据当前响应和加载状态选择内容、骨架或错误态。 */
export const EntityResponse = observer((props: IEntityResponseProps) => {
  const { store, children, skeleton, returnIndex, again = true, onAgain, load = false, size, loading, response, errorProps } = props;
  const curStore = useStore(store);
  const curLoading = loading ?? curStore?.loading;
  const curResponse = response ?? curStore?.response;

  const components = useComponents();

  const Loading = useMemo(() => (components?.Loading ?? (() => 'loading')), [components?.Loading]);

  const Skeleton = useMemo(() => (components?.Skeleton ?? (() => 'loading')), [components?.Skeleton]);

  const ErrorContent = useMemo(() => (components?.ErrorContent ?? (() => 'error')), [components?.ErrorContent]);

  const curOnAgain = useMemo(
    () => (again === false ? undefined : onAgain ?? curStore?.fetch),
    [again, onAgain, curStore?.fetch],
  );

  const isSkeleton = useMemo(() => curLoading && isVacuous(curResponse) && skeleton !== false, [curLoading, curResponse, skeleton]);

  const curChildren = useMemo(
    () => (load
      ? <Loading size={size} {...(typeof load === 'object' ? load : {})} loading={curLoading}>{children}</Loading>
      : children)
    , [load, Loading, size, curLoading, children],
  );

  if (isSkeleton) {
    return <Skeleton size={size} {...skeleton} />;
  }

  // 非加载阶段的空响应属于正常初始内容态，不能误判成请求失败。
  if (isVacuous(curResponse) || isSuccess(curResponse)) {
    return curChildren;
  }

  return (
    <ErrorContent
      {...errorProps}
      loading={curLoading}
      response={curResponse}
      returnIndex={returnIndex}
      size={size}
      onAgain={curOnAgain}
    />
  );
});
